import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import pluginWebpage from "grapesjs-preset-webpage";
import "./builder.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faFloppyDisk,
  faDesktop,
  faTabletScreenButton,
  faMobileScreenButton,
  faRotateLeft,
  faRotateRight,
  faTrashCan,
  faEye,
  faCode,
  faDownload,
  faCopy,
  faUpload,
  faFolderOpen,
  faFileCode,
  faBars,
  faSliders,
  faExpand,
  faCompress,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Swal from "sweetalert2";
import { API_URL } from "../../config";
import { registerCustomBlocks } from "./blocks";

const TemplateBuilderEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  const [templateName, setTemplateName] = useState(id ? "" : "New Visual Template");
  const [category, setCategory] = useState("Landing Page");
  const [activeDevice, setActiveDevice] = useState("Desktop");
  const [isSaved, setIsSaved] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(!!id);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Sidebar & Layout states
  const [isCrmSidebarOpen, setIsCrmSidebarOpen] = useState(true);
  const [isToolsSidebarOpen, setIsToolsSidebarOpen] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Export Modal state
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportedHtml, setExportedHtml] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  // Import Modal state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importTab, setImportTab] = useState("file"); // "file" | "code"
  const [importHtmlCode, setImportHtmlCode] = useState("");
  const [importMode, setImportMode] = useState("replace"); // "replace" | "append"
  const [selectedFileName, setSelectedFileName] = useState("");
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [customHeadLinks, setCustomHeadLinks] = useState([]);
  const [customCss, setCustomCss] = useState("");
  const fileInputRef = useRef(null);

  const editorRef = useRef(null);
  const editorContainerRef = useRef(null);

  // Initialize GrapesJS
  useEffect(() => {
    if (!editorContainerRef.current) return;

    const editor = grapesjs.init({
      container: editorContainerRef.current,
      height: "calc(100vh - 140px)",
      width: "auto",
      fromElement: false,
      storageManager: false,

      canvas: {
        styles: [
          "https://fonts.googleapis.com/css2?family=Roboto+Flex:wght@300;400;500;600;700;800;900&family=Inter:wght@400;500;600;700&family=Open+Sans:wght@400;600;700&display=swap",
        ],
      },

      deviceManager: {
        devices: [
          { name: "Desktop", width: "" },
          { name: "Tablet", width: "768px", widthMedia: "992px" },
          { name: "Mobile portrait", width: "375px", widthMedia: "480px" },
        ],
      },

      assetManager: {
        uploadFile: async (e) => {
          const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
          if (!files || !files.length) return;

          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const formData = new FormData();
            formData.append("file", file);

            try {
              const res = await axios.post(
                `${API_URL}settings/template/builder/upload-asset`,
                formData,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                  },
                }
              );

              const s3Url =
                res?.data?.url ||
                (Array.isArray(res?.data?.data) ? res.data.data[0] : res?.data?.data?.url) ||
                (typeof res?.data?.data === "string" ? res.data.data : null);

              if (s3Url) {
                editor.AssetManager.add({
                  src: s3Url,
                  name: file.name || "Uploaded Image",
                });
              } else {
                throw new Error("No S3 URL returned");
              }
            } catch (uploadErr) {
              console.warn("S3 upload failed, adding as local asset:", uploadErr);
              const reader = new FileReader();
              reader.onload = (uploadEvent) => {
                editor.AssetManager.add({
                  src: uploadEvent.target.result,
                  name: file.name || "Uploaded Image",
                });
              };
              reader.readAsDataURL(file);
            }
          }
        },
        assets: [
          {
            src: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80",
            name: "Dashboard Analytics",
          },
          {
            src: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80",
            name: "Team Discussion",
          },
          {
            src: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=800&q=80",
            name: "Trading Strategy",
          },
          {
            src: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80",
            name: "Analyst Avatar",
          },
        ],
      },

      styleManager: {
        sectors: [
          {
            name: "General",
            open: false,
            buildProps: ["float", "display", "position", "top", "right", "bottom", "left"],
          },
          {
            name: "Dimension",
            open: true,
            buildProps: [
              "width",
              "height",
              "max-width",
              "min-width",
              "max-height",
              "min-height",
              "margin",
              "padding",
            ],
          },
          {
            name: "Typography",
            open: true,
            buildProps: [
              "font-family",
              "font-size",
              "font-weight",
              "letter-spacing",
              "color",
              "line-height",
              "text-align",
              "text-decoration",
              "text-shadow",
            ],
          },
          {
            name: "Decorations",
            open: true,
            buildProps: [
              "background-color",
              "border-radius",
              "border",
              "box-shadow",
              "background",
            ],
          },
          {
            name: "Extra & Flexbox",
            open: false,
            buildProps: [
              "opacity",
              "transition",
              "transform",
              "flex-direction",
              "flex-wrap",
              "align-items",
              "justify-content",
              "cursor",
            ],
          },
        ],
      },

      plugins: [pluginWebpage],
      pluginsOpts: {
        [pluginWebpage]: {
          blocks: [],
        },
      },
    });

    // Register Custom Blocks
    registerCustomBlocks(editor);

    // Track Changes
    editor.on("component:add component:remove component:update style:update", () => {
      setIsSaved(false);
    });

    // Open block manager tab on load
    editor.on("load", () => {
      editor.runCommand("open-blocks");
    });

    editorRef.current = editor;

    // Load initial or fetched data
    if (id) {
      fetchTemplateData(editor, id);
    } else {
      // Light CRM Default Starter Section
      const defaultStarterHtml = `
        <section style="padding: 60px 20px; text-align: center; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; font-family: Inter, sans-serif; margin: 20px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);">
          <span style="display:inline-block; padding: 5px 16px; background: #e0e7ff; color: #4338ca; border-radius: 20px; font-size: 12px; font-weight: 700; margin-bottom: 18px; letter-spacing: 0.5px;">STREETGAINS RESEARCH</span>
          <h1 style="font-size: 36px; font-weight: 800; color: #0f172a; margin-bottom: 16px;">Welcome to Your Custom Template</h1>
          <p style="font-size: 16px; color: #64748b; max-width: 600px; margin: 0 auto 28px; line-height: 1.6;">Drag elements from the right sidebar or customize this starter section directly.</p>
          <a href="#" style="display: inline-block; padding: 12px 28px; background: #6366f1; color: #ffffff; text-decoration: none; border-radius: 8px; font-weight: 600;">Get Started Free</a>
        </section>
      `;
      editor.setComponents(defaultStarterHtml);
      setIsSaved(true);
    }

    return () => {
      editor.destroy();
    };
  }, [id]);

  // Fetch Template Data when editing
  const fetchTemplateData = async (editor, templateId) => {
    try {
      setLoadingData(true);
      const response = await axios.get(`${API_URL}settings/template/builder/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.data) {
        const data = response.data.data;
        setTemplateName(data.template_name || "");
        setCategory(data.category || "General");

        let loaded = false;
        if (data.components_json) {
          try {
            const parsed =
              typeof data.components_json === "string"
                ? JSON.parse(data.components_json)
                : data.components_json;

            if (parsed && (parsed.pages || (parsed.components && parsed.styles))) {
              editor.loadProjectData(parsed);
              loaded = true;
            } else if (parsed) {
              editor.setComponents(parsed);
              loaded = true;
            }
          } catch (e) {
            console.warn("Could not load components JSON, falling back:", e);
          }
        }

        if (!loaded && data.html_content) {
          try {
            const { bodyContent, extractedCss, linkTags } = parseHtmlDocument(data.html_content);
            editor.setComponents(bodyContent);
            if (extractedCss) {
              setCustomCss(extractedCss);
              editor.setStyle(extractedCss);
            }

            if (linkTags.length) {
              setCustomHeadLinks(linkTags);
              const canvasDoc = editor.Canvas.getDocument();
              if (canvasDoc && canvasDoc.head) {
                linkTags.forEach((tagHtml) => {
                  const div = document.createElement("div");
                  div.innerHTML = tagHtml;
                  const el = div.firstElementChild;
                  if (el) canvasDoc.head.appendChild(el);
                });
              }
            }
            loaded = true;
          } catch (e) {
            console.warn("Could not load html_content:", e);
          }
        }

        if (data.styles_json) {
          try {
            const styles =
              typeof data.styles_json === "string"
                ? JSON.parse(data.styles_json)
                : data.styles_json;
            if (styles && editor.setStyle) {
              editor.setStyle(styles);
            }
          } catch (e) {
            console.warn("Could not set styles JSON:", e);
          }
        }

        setIsSaved(true);
      }
    } catch (err) {
      console.error("Failed to load template data:", err);
      Swal.fire("Error", "Failed to load template data from server", "error");
    } finally {
      setLoadingData(false);
    }
  };

  // Helper to parse full HTML files and extract external fonts, CSS and body
  const parseHtmlDocument = (fullHtml) => {
    if (!fullHtml) return { bodyContent: "", extractedCss: "", linkTags: [], docTitle: "" };

    const parser = new DOMParser();
    const doc = parser.parseFromString(fullHtml, "text/html");

    // 1. Extract external links (e.g. Google Fonts, CDNs)
    const linkElements = Array.from(doc.querySelectorAll("link"));
    const linkTags = linkElements.map((l) => l.outerHTML);

    // 2. Extract Title
    const docTitle = doc.querySelector("title")?.textContent?.trim() || "";

    // 3. Extract all <style> text content
    const styleTags = Array.from(doc.querySelectorAll("style"));
    let extractedCss = "";
    styleTags.forEach((st) => {
      extractedCss += st.textContent + "\n";
      st.remove();
    });

    // 4. Extract body content
    let bodyContent = "";
    if (doc.body && doc.body.innerHTML.trim()) {
      bodyContent = doc.body.innerHTML;
    } else {
      bodyContent = fullHtml;
    }

    return {
      docTitle,
      bodyContent,
      extractedCss,
      linkTags,
    };
  };

  // Helper to generate complete standalone HTML document
  const generateCompleteHtml = () => {
    if (!editorRef.current) return "";
    const rawHtml = editorRef.current.getHtml();
    const rawCss = editorRef.current.getCss();
    const linksHtml = customHeadLinks.length ? customHeadLinks.join("\n  ") + "\n  " : "";
    
    // Combine custom CSS (with :root variables, resets, etc.) with GrapesJS CSS
    const combinedCss = customCss
      ? `${customCss}\n\n/* Visual Editor Styles */\n${rawCss}`
      : rawCss;

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${templateName.trim() || "Template"}</title>
  ${linksHtml}<style>
${combinedCss}
  </style>
</head>
<body>
${rawHtml}
</body>
</html>`;
  };

  // CRM Left Sidebar Toggle Handler
  const handleToggleCrmSidebar = () => {
    const pageContainer = document.getElementById("page-container");
    if (pageContainer) {
      pageContainer.classList.toggle("page-sidebar-minified");
      setIsCrmSidebarOpen(!pageContainer.classList.contains("page-sidebar-minified"));
      setTimeout(() => {
        if (editorRef.current) editorRef.current.refresh();
      }, 300);
    }
  };

  // Right Tools (Blocks & Styles) Sidebar Toggle Handler
  const handleToggleToolsSidebar = () => {
    const viewsContainer = document.querySelector(".gjs-pn-views-container");
    const viewsBar = document.querySelector(".gjs-pn-views");

    setIsToolsSidebarOpen((prev) => {
      const nextState = !prev;
      if (viewsContainer) {
        if (nextState) {
          viewsContainer.classList.remove("hidden-panel");
        } else {
          viewsContainer.classList.add("hidden-panel");
        }
      }
      if (viewsBar) {
        if (nextState) {
          viewsBar.classList.remove("hidden-panel");
        } else {
          viewsBar.classList.add("hidden-panel");
        }
      }
      setTimeout(() => {
        if (editorRef.current) editorRef.current.refresh();
      }, 100);
      return nextState;
    });
  };

  // Fullscreen Toggle
  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
        setIsFullscreen(false);
      }
    }
  };

  // Device Changer
  const handleDeviceChange = (deviceName) => {
    if (!editorRef.current) return;
    editorRef.current.setDevice(deviceName);
    setActiveDevice(deviceName);
  };

  // Undo & Redo
  const handleUndo = () => {
    if (editorRef.current && editorRef.current.UndoManager.hasUndo()) {
      editorRef.current.UndoManager.undo();
    }
  };

  const handleRedo = () => {
    if (editorRef.current && editorRef.current.UndoManager.hasRedo()) {
      editorRef.current.UndoManager.redo();
    }
  };

  // Clear Canvas
  const handleClear = () => {
    Swal.fire({
      title: "Clear Canvas?",
      text: "This will remove all components on the canvas.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, Clear",
    }).then((result) => {
      if (result.isConfirmed && editorRef.current) {
        editorRef.current.DomComponents.clear();
        editorRef.current.CssComposer.clear();
        setIsSaved(false);
      }
    });
  };

  // Preview Mode
  const handleTogglePreview = () => {
    if (!editorRef.current) return;
    if (editorRef.current.Commands.isActive("core:preview")) {
      editorRef.current.stopCommand("core:preview");
      setIsPreviewMode(false);
    } else {
      editorRef.current.runCommand("core:preview");
      setIsPreviewMode(true);
    }
  };

  // Open Export Modal
  const handleOpenExport = () => {
    const fullHtml = generateCompleteHtml();
    setExportedHtml(fullHtml);
    setIsExportOpen(true);
  };

  // Import File & Code Handlers
  const handleFileSelect = (file) => {
    if (!file) return;
    const isHtmlOrText =
      file.name.endsWith(".html") ||
      file.name.endsWith(".htm") ||
      file.name.endsWith(".txt") ||
      file.type === "text/html" ||
      file.type === "text/plain";

    if (!isHtmlOrText) {
      Swal.fire("Invalid File", "Please select a valid .html or .htm file.", "warning");
      return;
    }

    setSelectedFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setImportHtmlCode(content);
    };
    reader.onerror = () => {
      Swal.fire("Error", "Failed to read the selected file.", "error");
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleExecuteImport = () => {
    if (!editorRef.current) return;
    const rawCode = importHtmlCode.trim();
    if (!rawCode) {
      Swal.fire("No Content", "Please upload a file or paste HTML code to import.", "warning");
      return;
    }

    try {
      const { docTitle, bodyContent, extractedCss, linkTags } = parseHtmlDocument(rawCode);

      if (importMode === "replace") {
        editorRef.current.setComponents(bodyContent);
        if (extractedCss) {
          editorRef.current.setStyle(extractedCss);
        }
      } else {
        editorRef.current.addComponents(bodyContent);
        if (extractedCss) {
          editorRef.current.addStyle(extractedCss);
        }
      }

      // Inject external links and extracted CSS directly into canvas iframe head
      const canvasDoc = editorRef.current.Canvas.getDocument();
      if (canvasDoc && canvasDoc.head) {
        linkTags.forEach((tagHtml) => {
          const div = document.createElement("div");
          div.innerHTML = tagHtml;
          const el = div.firstElementChild;
          if (el) canvasDoc.head.appendChild(el);
        });

        if (extractedCss) {
          let styleEl = canvasDoc.getElementById("gjs-imported-custom-styles");
          if (!styleEl) {
            styleEl = canvasDoc.createElement("style");
            styleEl.id = "gjs-imported-custom-styles";
            canvasDoc.head.appendChild(styleEl);
          }
          if (importMode === "replace") {
            styleEl.innerHTML = extractedCss;
          } else {
            styleEl.innerHTML += "\n" + extractedCss;
          }
        }
      }

      if (linkTags.length) {
        setCustomHeadLinks(linkTags);
      }

      if (extractedCss) {
        setCustomCss(extractedCss);
      }

      if (docTitle && (!templateName || templateName === "New Visual Template")) {
        setTemplateName(docTitle);
      }

      setIsSaved(false);
      setIsImportOpen(false);
      setImportHtmlCode("");
      setSelectedFileName("");
      if (fileInputRef.current) fileInputRef.current.value = "";

      Swal.fire({
        icon: "success",
        title: "HTML Imported!",
        text: "Template & styles loaded successfully onto canvas with full fidelity.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Failed to import HTML into canvas:", err);
      Swal.fire("Import Error", "Failed to parse and import HTML content into editor.", "error");
    }
  };

  // Save to Database
  const handleSaveToDatabase = async () => {
    if (!editorRef.current) return;

    if (!templateName.trim()) {
      Swal.fire("Validation Error", "Please provide a name for this template.", "warning");
      return;
    }

    try {
      setSaving(true);
      const combinedHtml = generateCompleteHtml();
      let componentsJson = "";
      try {
        if (editorRef.current.getProjectData) {
          componentsJson = JSON.stringify(editorRef.current.getProjectData());
        } else {
          componentsJson = JSON.stringify(editorRef.current.getComponents());
        }
      } catch {
        componentsJson = JSON.stringify(editorRef.current.getComponents());
      }
      const stylesJson = JSON.stringify(editorRef.current.getStyle());

      const payload = {
        template_name: templateName.trim(),
        category,
        html_content: combinedHtml,
        components_json: componentsJson,
        styles_json: stylesJson,
        status: 1,
      };

      const url = id
        ? `${API_URL}settings/template/builder/save?id=${id}`
        : `${API_URL}settings/template/builder/save`;

      const response = await axios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success || response.data?.data) {
        setIsSaved(true);
        Swal.fire({
          icon: "success",
          title: "Template Saved!",
          text: "Design saved successfully to the database.",
          timer: 1800,
          showConfirmButton: false,
        });

        if (!id && response.data?.data?.id) {
          navigate(`/Settings/TemplateBuilder/edit/${response.data.data.id}`, {
            replace: true,
          });
        }
      }
    } catch (err) {
      console.error("Save error:", err);
      Swal.fire(
        "Save Failed",
        err.response?.data?.message || "Failed to save template to database.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="p-0">
        <div className="crm-builder-wrapper">
          {/* CRM Light Theme Top Toolbar (2-Row Layout) */}
          <header className="crm-builder-header">
            {/* ROW 1: Navigation, Template Info, Status & Primary Actions */}
            <div className="toolbar-row toolbar-row-top">
              <div className="toolbar-section">
                <Link
                  to="/Settings/TemplateBuilder"
                  className="btn btn-default"
                  title="Back to Template List"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  <span>Templates</span>
                </Link>

                <div className="toolbar-divider" />

                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => {
                    setTemplateName(e.target.value);
                    setIsSaved(false);
                  }}
                  placeholder="Enter template name..."
                  className="template-name-input"
                />

                <select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setIsSaved(false);
                  }}
                  className="category-select"
                >
                  <option value="Landing Page">Landing Page</option>
                  <option value="Email Template">Email Template</option>
                  <option value="Promotion">Promotion</option>
                  <option value="Newsletter">Newsletter</option>
                  <option value="General">General</option>
                </select>
              </div>

              <div className="toolbar-section">
                <span
                  className={`status-pill ${
                    isSaved ? "bg-success text-white" : "bg-warning text-dark"
                  }`}
                >
                  {isSaved ? "● Saved" : "○ Unsaved"}
                </span>

                <div className="toolbar-divider" />

                <button
                  type="button"
                  className="btn btn-default text-primary"
                  onClick={() => setIsImportOpen(true)}
                  title="Import External HTML / File"
                >
                  <FontAwesomeIcon icon={faUpload} />
                  <span>Import HTML</span>
                </button>

                <button
                  type="button"
                  className="btn btn-default text-primary"
                  onClick={handleOpenExport}
                  title="Export Combined HTML"
                >
                  <FontAwesomeIcon icon={faCode} />
                  <span>Export HTML</span>
                </button>

                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleSaveToDatabase}
                  disabled={saving}
                  style={{ minWidth: "120px" }}
                >
                  {saving ? (
                    <span>Saving...</span>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faFloppyDisk} />
                      <span>Save Design</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* ROW 2: Canvas Devices, History Actions, Preview & Sidebar Tools */}
            <div className="toolbar-row toolbar-row-bottom">
              <div className="toolbar-section">
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn ${
                      activeDevice === "Desktop" ? "btn-primary" : "btn-default"
                    }`}
                    onClick={() => handleDeviceChange("Desktop")}
                    title="Desktop View"
                  >
                    <FontAwesomeIcon icon={faDesktop} />
                    <span>Desktop</span>
                  </button>
                  <button
                    type="button"
                    className={`btn ${
                      activeDevice === "Tablet" ? "btn-primary" : "btn-default"
                    }`}
                    onClick={() => handleDeviceChange("Tablet")}
                    title="Tablet View"
                  >
                    <FontAwesomeIcon icon={faTabletScreenButton} />
                    <span>Tablet</span>
                  </button>
                  <button
                    type="button"
                    className={`btn ${
                      activeDevice === "Mobile portrait" ? "btn-primary" : "btn-default"
                    }`}
                    onClick={() => handleDeviceChange("Mobile portrait")}
                    title="Mobile View"
                  >
                    <FontAwesomeIcon icon={faMobileScreenButton} />
                    <span>Mobile</span>
                  </button>
                </div>

                <div className="toolbar-divider" />

                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={handleUndo}
                    title="Undo"
                  >
                    <FontAwesomeIcon icon={faRotateLeft} />
                    <span>Undo</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={handleRedo}
                    title="Redo"
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                    <span>Redo</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-default text-danger"
                    onClick={handleClear}
                    title="Clear Canvas"
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                    <span>Clear</span>
                  </button>
                </div>
              </div>

              <div className="toolbar-section">
                <button
                  type="button"
                  className={`btn ${isPreviewMode ? "btn-warning" : "btn-default"}`}
                  onClick={handleTogglePreview}
                  title={isPreviewMode ? "Exit Preview" : "Preview Mode"}
                >
                  <FontAwesomeIcon icon={faEye} />
                  <span>{isPreviewMode ? "Edit Canvas" : "Preview"}</span>
                </button>

                <button
                  type="button"
                  className={`btn ${isToolsSidebarOpen ? "btn-primary" : "btn-default"}`}
                  onClick={handleToggleToolsSidebar}
                  title={isToolsSidebarOpen ? "Hide Tools Panel" : "Show Tools Panel"}
                >
                  <FontAwesomeIcon icon={faSliders} />
                  <span>{isToolsSidebarOpen ? "Hide Tools" : "Show Tools"}</span>
                </button>

                <button
                  type="button"
                  className="btn btn-icon-only btn-default"
                  onClick={handleToggleFullscreen}
                  title={isFullscreen ? "Exit Fullscreen" : "Fullscreen Mode"}
                >
                  <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
                </button>
              </div>
            </div>
          </header>

          {/* Editor Main Canvas Container */}
          <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
            {loadingData && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255, 255, 255, 0.8)",
                  zIndex: 200,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div className="spinner-border text-primary mb-3" role="status"></div>
                <p style={{ color: "#495057", fontSize: "14px", fontWeight: "600" }}>
                  Loading template design...
                </p>
              </div>
            )}
            <div ref={editorContainerRef} style={{ height: "100%", width: "100%" }} />
          </div>

          {/* Import Code/File Modal */}
          {isImportOpen && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content" style={{ borderRadius: "8px" }}>
                  <div className="modal-header d-flex justify-content-between align-items-center py-3 bg-light">
                    <h5 className="modal-title m-0 font-weight-bold">
                      <FontAwesomeIcon icon={faUpload} className="me-2 text-primary" />
                      Import HTML Template
                    </h5>
                    <button
                      type="button"
                      className="close"
                      onClick={() => setIsImportOpen(false)}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        lineHeight: 1,
                        color: "#6c757d",
                        cursor: "pointer",
                        opacity: 0.8,
                        padding: "0 8px",
                      }}
                      title="Close"
                    >
                      &times;
                    </button>
                  </div>

                  <div className="modal-body p-3">
                    {/* Tab Navigation */}
                    <div className="d-flex mb-3 gap-2 border-bottom pb-2">
                      <button
                        type="button"
                        className={`btn btn-sm ${importTab === "file" ? "btn-primary" : "btn-default"}`}
                        onClick={() => setImportTab("file")}
                        style={{ borderRadius: "4px" }}
                      >
                        <FontAwesomeIcon icon={faFolderOpen} className="me-1" />
                        Upload .html File
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${importTab === "code" ? "btn-primary" : "btn-default"}`}
                        onClick={() => setImportTab("code")}
                        style={{ borderRadius: "4px" }}
                      >
                        <FontAwesomeIcon icon={faFileCode} className="me-1" />
                        Paste HTML Code
                      </button>
                    </div>

                    {importTab === "file" && (
                      <div className="mb-3">
                        <div
                          onDragOver={(e) => {
                            e.preventDefault();
                            setIsDraggingFile(true);
                          }}
                          onDragLeave={() => setIsDraggingFile(false)}
                          onDrop={handleFileDrop}
                          onClick={() => fileInputRef.current?.click()}
                          style={{
                            border: `2px dashed ${isDraggingFile ? "#348fe2" : "#cbd5e1"}`,
                            borderRadius: "8px",
                            padding: "24px 16px",
                            textAlign: "center",
                            background: isDraggingFile ? "#f0f7ff" : "#f8fafc",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".html,.htm,.txt"
                            style={{ display: "none" }}
                            onChange={handleFileInputChange}
                          />
                          <FontAwesomeIcon
                            icon={faUpload}
                            style={{
                              fontSize: "28px",
                              color: isDraggingFile ? "#348fe2" : "#94a3b8",
                              marginBottom: "8px",
                            }}
                          />
                          <p style={{ margin: "0 0 4px 0", fontWeight: "600", fontSize: "14px", color: "#334155" }}>
                            Click to choose a file or drag & drop here
                          </p>
                          <span style={{ fontSize: "12px", color: "#64748b" }}>
                            Supports .html, .htm files (Email templates, Landing pages, Custom HTML)
                          </span>

                          {selectedFileName && (
                            <div className="mt-3 p-2 bg-white border rounded d-inline-flex align-items-center gap-2">
                              <FontAwesomeIcon icon={faFileCode} className="text-success" />
                              <strong style={{ fontSize: "13px", color: "#0f172a" }}>{selectedFileName}</strong>
                              <span className="badge bg-success text-white">Loaded</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Code Preview / Paste Textarea */}
                    <div className="mb-3">
                      <label className="form-label font-weight-bold small text-muted mb-1">
                        {importTab === "file"
                          ? "File HTML Content Preview / Edit:"
                          : "Paste Your HTML Code Below:"}
                      </label>
                      <textarea
                        rows={9}
                        value={importHtmlCode}
                        onChange={(e) => setImportHtmlCode(e.target.value)}
                        placeholder="<!DOCTYPE html><html><body><h1>Paste your custom HTML here...</h1></body></html>"
                        style={{
                          width: "100%",
                          fontFamily: "monospace",
                          fontSize: "12px",
                          background: "#f8f9fa",
                          color: "#242a30",
                          padding: "10px",
                          borderRadius: "6px",
                          border: "1px solid #ccd0d4",
                          resize: "vertical",
                          outline: "none",
                        }}
                      />
                    </div>

                    {/* Import Mode Options */}
                    <div className="p-2.5 bg-light rounded border">
                      <span className="font-weight-bold small text-dark d-block mb-2">Import Option:</span>
                      <div className="d-flex gap-4">
                        <label
                          className="d-flex align-items-center gap-2 mb-0"
                          style={{ cursor: "pointer", fontSize: "13px" }}
                        >
                          <input
                            type="radio"
                            name="importMode"
                            value="replace"
                            checked={importMode === "replace"}
                            onChange={(e) => setImportMode(e.target.value)}
                          />
                          <span>
                            <strong>Replace canvas</strong> (Clears existing components)
                          </span>
                        </label>
                        <label
                          className="d-flex align-items-center gap-2 mb-0"
                          style={{ cursor: "pointer", fontSize: "13px" }}
                        >
                          <input
                            type="radio"
                            name="importMode"
                            value="append"
                            checked={importMode === "append"}
                            onChange={(e) => setImportMode(e.target.value)}
                          />
                          <span>
                            <strong>Append</strong> (Adds to the bottom of canvas)
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer py-2 bg-light d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-default btn-sm"
                      onClick={() => {
                        setImportHtmlCode("");
                        setSelectedFileName("");
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      disabled={!importHtmlCode}
                    >
                      Clear Input
                    </button>
                    <div>
                      <button
                        type="button"
                        className="btn btn-default btn-sm me-2"
                        onClick={() => setIsImportOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleExecuteImport}
                        disabled={!importHtmlCode.trim()}
                      >
                        <FontAwesomeIcon icon={faUpload} className="me-1" />
                        Import to Canvas
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Export Code Modal */}
          {isExportOpen && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content" style={{ borderRadius: "8px" }}>
                  <div className="modal-header d-flex justify-content-between align-items-center py-3 bg-light">
                    <h5 className="modal-title m-0 font-weight-bold">
                      <FontAwesomeIcon icon={faCode} className="me-2 text-primary" />
                      Combined HTML Content
                    </h5>
                    <button
                      type="button"
                      className="close"
                      onClick={() => setIsExportOpen(false)}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        lineHeight: 1,
                        color: "#6c757d",
                        cursor: "pointer",
                        opacity: 0.8,
                        padding: "0 8px",
                      }}
                      title="Close"
                    >
                      &times;
                    </button>
                  </div>

                  <div className="modal-body p-3">
                    <p className="text-muted small mb-2">
                      Complete HTML code with embedded styles ready for direct web or email display:
                    </p>
                    <textarea
                      readOnly
                      rows={16}
                      value={exportedHtml}
                      style={{
                        width: "100%",
                        fontFamily: "monospace",
                        fontSize: "13px",
                        background: "#f8f9fa",
                        color: "#242a30",
                        padding: "12px",
                        borderRadius: "6px",
                        border: "1px solid #ccd0d4",
                        resize: "vertical",
                        outline: "none",
                      }}
                    />
                  </div>

                  <div className="modal-footer py-2 bg-light d-flex justify-content-between">
                    <div>
                      {copySuccess && (
                        <span className="text-success small me-2 font-weight-bold">
                          ✓ Copied to clipboard!
                        </span>
                      )}
                    </div>
                    <div>
                      <button
                        className="btn btn-default btn-sm me-2"
                        onClick={() => setIsExportOpen(false)}
                      >
                        Close
                      </button>
                      <button
                        className="btn btn-primary btn-sm me-2"
                        onClick={() => {
                          navigator.clipboard.writeText(exportedHtml);
                          setCopySuccess(true);
                          setTimeout(() => setCopySuccess(false), 2000);
                        }}
                      >
                        <FontAwesomeIcon icon={faCopy} className="me-1" /> Copy HTML
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => {
                          const blob = new Blob([exportedHtml], { type: "text/html" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `${(templateName || "template")
                            .toLowerCase()
                            .replace(/\s+/g, "_")}.html`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }}
                      >
                        <FontAwesomeIcon icon={faDownload} className="me-1" /> Download .html
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateBuilderEditor;
